import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import client, {
  API, formatApiError, fmtTime, fmtDay, dayKey, TYPE_LABEL, fmtHours,
  workedMinutes, hasUnclosedBreak, isoWeekStart, isoWeekId,
} from "../../../lib/api";

/* ---------- Data mirror: export + import (super only) ---------- */
function DataTransfer() {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [pending, setPending] = useState(null); // { fileName, size, bundle }
  const fileRef = useRef(null);

  const doExport = async () => {
    setBusy(true);
    try {
      const { data } = await client.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `register-export-${today}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded — treat this file as private");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      if (!bundle || !bundle.collections) {
        toast.error("That doesn't look like a Register export");
        return;
      }
      setPending({ fileName: file.name, size: file.size, bundle });
      setConfirmText("");
    } catch {
      toast.error("Couldn't read the file — is it a valid JSON export?");
    }
  };

  const doImport = async () => {
    if (!pending) return;
    if (confirmText !== "REPLACE") {
      toast.error('Type REPLACE to confirm');
      return;
    }
    setBusy(true);
    try {
      await client.post("/admin/import", { ...pending.bundle, confirm: "REPLACE" });
      toast.success("Data replaced — sign in again with the imported PIN");
      setPending(null);
      setConfirmText("");
      // Force a re-login because owner records were replaced
      setTimeout(() => {
        localStorage.removeItem("owner_token");
        window.location.href = "/owner-access";
      }, 900);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const counts = pending?.bundle?.collections
    ? Object.entries(pending.bundle.collections).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.length : 0}`)
    : [];

  return (
    <div className="bg-panel border border-line rounded-xl p-4.5" data-testid="settings-data-transfer">
      <div className="caps text-muted-jewel mb-2.5">Data mirror</div>
      <p className="text-muted-jewel text-[13px] mb-3">
        Copy an entire environment (owners, staff, events, pay marks, settings, audits) — handy for showing preview to a client. The export file contains hashed PINs, so keep it private.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <button
          onClick={doExport}
          disabled={busy}
          data-testid="data-export-btn"
          className="seal-primary flex-1 rounded-lg py-2.5 text-[14px] font-semibold disabled:opacity-40"
        >
          Download export
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid="data-import-pick"
          className="flex-1 rounded-lg py-2.5 text-[14px] font-semibold border border-line text-ivory hover:border-gold-deep"
        >
          Import a file…
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          onChange={onFilePicked}
          className="hidden"
          data-testid="data-import-file"
        />
      </div>

      {pending && (
        <div className="border border-jewel-red/60 rounded-lg p-3" data-testid="data-import-confirm">
          <div className="text-ivory text-[14px] font-semibold mb-1">
            About to replace everything with this file
          </div>
          <div className="text-muted-jewel text-[12px] mb-2">
            {pending.fileName} · {(pending.size / 1024).toFixed(1)} KB · exported {pending.bundle.exported_at?.slice(0, 10) || "unknown"}
          </div>
          <div className="text-muted-jewel text-[12px] mb-3">
            Will insert — {counts.join(" · ")}
          </div>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder="Type REPLACE to confirm"
            data-testid="data-import-confirm-input"
            className="w-full px-3 py-2.5 bg-panel-soft text-ivory border border-line rounded-lg text-[15px] focus:outline-none font-mono mb-2"
          />
          <div className="flex gap-2">
            <button
              onClick={doImport}
              disabled={busy || confirmText !== "REPLACE"}
              data-testid="data-import-run"
              className="flex-1 rounded-lg py-2.5 text-[14px] font-semibold bg-jewel-red text-white disabled:opacity-40"
            >
              Replace all data
            </button>
            <button
              onClick={() => { setPending(null); setConfirmText(""); }}
              data-testid="data-import-cancel"
              className="flex-1 rounded-lg py-2.5 text-[14px] font-semibold border border-line text-muted-jewel hover:text-ivory"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTransfer;
